import { authService } from '../auth/service';
import { PHONE_APP_AUTH_TOKEN, LEVEL } from '../envs';

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: Record<string, unknown>;
  headers?: Record<string, string>;
  useAuthService?: boolean;
  deduplicate?: boolean;
}

// In-flight request deduplication for GET requests
const inFlightRequests = new Map<string, Promise<unknown>>();

const getRequestKey = (url: string, method: string): string => {
  return `${method}:${url}`;
};

export const makeRequest = async <T = unknown>(
  url: string,
  options: RequestOptions = {},
): Promise<T> => {
  const {
    method = 'GET',
    body,
    headers = {},
    useAuthService = false,
    deduplicate = true,
  } = options;

  // Only deduplicate GET requests (POST/PUT/DELETE have side effects)
  const shouldDeduplicate = deduplicate && method === 'GET';
  const requestKey = getRequestKey(url, method);

  // Return existing in-flight request if available
  if (shouldDeduplicate && inFlightRequests.has(requestKey)) {
    return inFlightRequests.get(requestKey) as Promise<T>;
  }

  const defaultHeaders: Record<string, string> = {
    'Accept': 'application/json',
    'Content-Type': 'application/json',
  };

  // Add ngrok bypass header only for ngrok URLs in non-production environments
  const isNgrokRequest = url.includes('ngrok-free.dev') || url.includes('ngrok.io');
  if (isNgrokRequest && LEVEL !== 'production' && LEVEL !== 'test') {
    defaultHeaders['ngrok-skip-browser-warning'] = 'true';
  }

  const authHeader = useAuthService ?
    { Authorization: authService.getAuthHeader() } :
    { Authorization: `Token ${PHONE_APP_AUTH_TOKEN}` };

  const requestOptions: RequestInit = {
    method,
    headers: {
      ...defaultHeaders,
      ...authHeader,
      ...headers,
    },
  };

  if (body && method !== 'GET') {
    requestOptions.body = JSON.stringify(body);
  }

  // Create the request promise
  const requestPromise = (async(): Promise<T> => {
    try {
      const response = await fetch(url, requestOptions);

      if (!response.ok) {
        const baseMessage = `Request failed: ${response.status} ${response.statusText}`;
        let errorMessage = baseMessage;

        try {
          const errorData = await response.json();

          // Handle different error formats
          if (errorData.error) {
            errorMessage = `${baseMessage} - ${errorData.error}`;
          } else if (errorData.detail) {
            errorMessage = `${baseMessage} - ${errorData.detail}`;
          } else if (typeof errorData === 'object') {
            // Format validation errors from Django REST Framework
            const validationErrors = Object.entries(errorData)
              .map(([field, errors]) => {
                if (Array.isArray(errors)) {
                  return `${field}: ${errors.join(', ')}`;
                }
                return `${field}: ${errors}`;
              })
              .join('; ');

            if (validationErrors) {
              errorMessage = `${baseMessage} - ${validationErrors}`;
            }
          }
        } catch {
          // If response is not JSON, use default error message
        }
        throw new Error(errorMessage);
      }

      // Handle 204 No Content (common for DELETE requests)
      if (response.status === 204) {
        return undefined as T;
      }

      return response.json();
    } finally {
      // Cleanup: remove from in-flight requests when done
      if (shouldDeduplicate) {
        inFlightRequests.delete(requestKey);
      }
    }
  })();

  // Store in-flight request for deduplication
  if (shouldDeduplicate) {
    inFlightRequests.set(requestKey, requestPromise);
  }

  return requestPromise;
};
